import React, { useEffect } from "react";
import { RouteComponentProps, navigate } from "@reach/router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import { useCurrentCompanyId } from "hooks/auth";
import { getDocument } from "../api/insuranceVerificationApi";
import { insuranceVerificationKeys } from "../api/insuranceVerificationKeys";
import { PendingVerificationBox } from "../components/PendingVerificationBox";
import { ConfirmExtractedFields } from "../components/ConfirmExtractedFields";
import { BaseErrorPage } from "newui/features/ErrorPages/components/BaseErrorPage";
import { LOCAL_STORAGE_KEYS } from "newui/constants/localStorageKeys";
import type { InsuranceOcrJobState } from "../types/InsuranceOcrJob.type";

interface ConfirmInsuranceDocumentPageProps extends RouteComponentProps {
  documentId?: string;
}

const ACTIVE_STATES: InsuranceOcrJobState[] = ["queued", "running"];
const TERMINAL_STATES: InsuranceOcrJobState[] = ["succeeded", "soft_failed", "failed"];

export const ConfirmInsuranceDocumentPage: React.FC<ConfirmInsuranceDocumentPageProps> = ({
  documentId: documentIdParam,
}) => {
  const companyId = useCurrentCompanyId() as number;
  const documentId = Number(documentIdParam);
  const queryClient = useQueryClient();

  const searchParams = new URLSearchParams(window.location.search);
  const ocrJobId = searchParams.get("ocr_job") ? Number(searchParams.get("ocr_job")) : undefined;

  const {
    data: document,
    isLoading,
    error,
  } = useQuery({
    queryKey: insuranceVerificationKeys.document(companyId, documentId),
    queryFn: () => getDocument(companyId, documentId),
    select: (res) => res.data.data,
    enabled: !isNaN(companyId) && !isNaN(documentId),
    retry: false,
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    const status = (error as { response?: { status?: number } })?.response?.status;

    if (status === 401) {
      const currentUrl = window.location.pathname + window.location.search;
      localStorage.setItem(LOCAL_STORAGE_KEYS.REDIRECT_PATH, currentUrl);
      navigate("/login");
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton count={5} />
      </div>
    );
  }

  const status = (error as { response?: { status?: number } })?.response?.status;

  if (error && status === 403) {
    return (
      <BaseErrorPage errorCode="403" title="Access Denied">
        You do not have permission to view this document.
      </BaseErrorPage>
    );
  }

  if (error) {
    return null;
  }

  if (!document) {
    return null;
  }

  const ocrJob = document.ocr_job;
  const jobState = ocrJob?.state;

  if (!jobState || ACTIVE_STATES.includes(jobState)) {
    return (
      <PendingVerificationBox
        documentId={documentId}
        initialState={jobState ?? "queued"}
        onComplete={() => {
          queryClient.invalidateQueries({
            queryKey: insuranceVerificationKeys.document(companyId, documentId),
          });
        }}
      />
    );
  }

  if (TERMINAL_STATES.includes(jobState)) {
    return (
      <ConfirmExtractedFields
        documentId={documentId}
        extraInfo={document.extra_info ?? {}}
        ocrJobState={jobState}
        ocrJobId={ocrJobId ?? ocrJob?.id}
      />
    );
  }

  return null;
};
